import { URLS } from '@/lib/constants';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { Platform } from 'react-native';

export type DrawerItem = {
  icon?: string;
  label: string;
  href?: string;
  externalUrl?: string; // For external links (opens in browser)
  onPress?: () => void;
  active?: boolean;
};

type DrawerContextType = {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  drawerItems: DrawerItem[];
  setDrawerItems: (items: DrawerItem[]) => void;
  resetDrawerItems: () => void;
  // Shared overlays (sheets) triggered from bottom bar / drawer
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
  openSearch: () => void;
  liveSessionsOpen: boolean;
  setLiveSessionsOpen: (open: boolean) => void;
  openLiveSessions: () => void;
  bookmarksOpen: boolean;
  setBookmarksOpen: (open: boolean) => void;
  openBookmarks: () => void;
  announcementsOpen: boolean;
  setAnnouncementsOpen: (open: boolean) => void;
  openAnnouncements: () => void;
  // Dismisses every shared overlay (search / live / bookmarks / announcements /
  // drawer). Navigation itself is handled by the router, not here.
  closeAllOverlays: () => void;
};

// Default drawer items (shown if screen doesn't customize)
export const DEFAULT_DRAWER_ITEMS: DrawerItem[] = Platform.OS === 'web' ? [
  // Mobile Web: Live Sessions is already in the header, so exclude from drawer
  { label: 'Dashboard', externalUrl: URLS.DASHBOARD },
  { label: 'Explore', externalUrl: URLS.EXPLORE },
  { label: 'The Hang', externalUrl: URLS.COMMUNITY },
] : [
  // Native: Live & Bookmarks & Search live in the bottom bar, so the drawer
  // only carries account / external destinations.
  { label: 'Dashboard', externalUrl: URLS.DASHBOARD },
  { label: 'Explore', externalUrl: URLS.EXPLORE },
  { label: 'The Hang', externalUrl: URLS.COMMUNITY },
];

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export function DrawerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [drawerItems, setDrawerItems] = useState<DrawerItem[]>(DEFAULT_DRAWER_ITEMS);
  const [searchOpen, setSearchOpen] = useState(false);
  const [liveSessionsOpen, setLiveSessionsOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [announcementsOpen, setAnnouncementsOpen] = useState(false);

  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => setIsOpen(false);
  const toggleDrawer = () => setIsOpen(prev => !prev);
  const resetDrawerItems = () => setDrawerItems(DEFAULT_DRAWER_ITEMS);
  const openSearch = () => setSearchOpen(true);
  const openLiveSessions = () => setLiveSessionsOpen(true);
  const openBookmarks = () => setBookmarksOpen(true);
  const openAnnouncements = () => setAnnouncementsOpen(true);
  const closeAllOverlays = () => {
    setIsOpen(false);
    setSearchOpen(false);
    setLiveSessionsOpen(false);
    setBookmarksOpen(false);
    setAnnouncementsOpen(false);
  };

  return (
    <DrawerContext.Provider
      value={{
        isOpen,
        openDrawer,
        closeDrawer,
        toggleDrawer,
        drawerItems,
        setDrawerItems,
        resetDrawerItems,
        searchOpen,
        setSearchOpen,
        openSearch,
        liveSessionsOpen,
        setLiveSessionsOpen,
        openLiveSessions,
        bookmarksOpen,
        setBookmarksOpen,
        openBookmarks,
        announcementsOpen,
        setAnnouncementsOpen,
        openAnnouncements,
        closeAllOverlays,
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
}

export function useDrawer() {
  const context = useContext(DrawerContext);
  if (context === undefined) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }
  return context;
}
